import "./styles/WasteDashboard.css";
import React, { useState } from "react";
import {
  Box,
  Typography,
  styled,
  Paper,
  Modal,
  IconButton,
  Fade,
  Button,
  keyframes,
  Backdrop,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";

// Import separated section components
import {
  KeyFeatures,
  SuthraPunjabProcess,
  Impact,
  DigitalMonitoring,
  WasteToValueProjects,
  CommunityMobilization,
} from "./Sections";

const SuthraPunjab = () => {
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [showSections, setShowSections] = useState(false);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const [, setIsVideoPlaying] = useState(false);
  const [openProjectModal, setOpenProjectModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const videoRef = React.useRef(null);

  // Section data for main view
  const sections = [
    {
      id: "features",
      title: "Key Features",
      icon: "🔑",
      description: "6 Advanced Features",
      bgGradient: "linear-gradient(135deg, #2d8659 0%, #000 50%, #4caf50 100%)",
    },
    {
      id: "process",
      title: "Suthra Punjab Process",
      icon: "⚙️",
      description: "6 Process Steps",
      bgGradient:
        "linear-gradient(135deg, #000000ff 0%, #66bb6a 50%, #000000ff 100%)",
    },
    {
      id: "impact",
      title: "Impact of Suthra Punjab",
      icon: "♻️",
      description: "6 Impact Areas",
      bgGradient: "linear-gradient(135deg, #2d8659 0%, #000 50%, #4caf50 100%)",
    },
    {
      id: "DigitalMonitoring",
      title: "Smart Waste Management System",
      icon: "📊",
      description: "8 Monitoring Areas",
      bgGradient:
        "linear-gradient(135deg, #000000ff 0%, #66bb6a 50%, #000000ff 100%)",
    },
    {
      id: "Waste-to-ValueProjects",
      title: "Waste-to-Value Projects",
      icon: "♻️",
      description: "8 Innovative Projects",
      bgGradient: "linear-gradient(135deg, #2d8659 0%, #000 50%, #4caf50 100%)",
    },
    {
      id: "community-mobilization",
      title: "Community Mobilization",
      icon: "📢",
      description: "Empowering the Nation",
      bgGradient: "linear-gradient(135deg, #2d8659 0%, #000 50%, #4caf50 100%)",
    },
  ];

  // Handle image click
  const handleImageClick = () => {
    setShowSections(true);
    setIsVideoVisible(true);
  };

  // Handle video end
  const handleVideoEnd = () => {
    setIsVideoPlaying(false);
  };

  // Handle section click
  const handleSectionClick = (sectionId) => {
    setSelectedSection(sectionId);
    setOpenModal(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedSection(null);
    setOpenImageModal(false);
    setSelectedImage(null);
  };

  // Handle image click in monitoring card
  const handleImageClick2 = (bgImage, e) => {
    e.stopPropagation();
    const imageUrl = bgImage.replace(/url\(['"]?([^'")]+)['"]?\)/g, "$1");
    setSelectedImage(imageUrl);
    setOpenImageModal(true);
  };

  // Handle project button click - open bottom modal
  const handleButtonClick = (item) => {
    setSelectedProject(item);
    setOpenProjectModal(true);
    // Close the main modal when opening project modal
    setOpenModal(true);
  };

  // Handle close project modal
  const handleCloseProjectModal = () => {
    setOpenProjectModal(false);
    setSelectedProject(null);
  };

  // Handle close image modal
  const handleCloseImageModal = () => {
    setOpenImageModal(false);
    setSelectedImage(null);
  };

  const moveLogoLeft = keyframes`
  from {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(1);
    width: 60%;
    height: 60%;
  }
  to {
    top: 40%;
    right: auto;
    left: 0%;
    transform: translate(0, -50%) scale(0.5);
    width: 35%;
    height: 45%;
  }
`;

  const assembleFromPieces = keyframes`
    0% {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.3);
    }
    50% {
    }
    100% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  `;
  // Styled Components
  const PageWrapper = styled(Box)({
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    overflow: "hidden",
    backgroundImage: "url('images/SuthraBackground.jpg')",
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
  });

  const CenteredLogo = styled(Box)({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "50%",
    height: "60%",
    backgroundImage: "url('images/SuthraPunjab.png')",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundColor: "#000",
    cursor: "pointer",
    borderRadius: "25px",
    animation: `${assembleFromPieces} 1.5s cubic-bezier(0.4, 0, 0.2, 1) forwards`,
    boxShadow:
      "0 25px 50px rgba(76, 175, 80, 0.3), 0 0 0 15px rgba(255, 255, 255, 0.1)",
    zIndex: 10,
    transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translate(-50%, -50%) scale(1.05)",
      boxShadow:
        "0 30px 60px rgba(10, 65, 12, 0.4), 0 0 0 15px rgba(255, 255, 255, 0.2)",
    },
    "&.moved-left": {
      animation: `${moveLogoLeft} 1s cubic-bezier(0.4, 0, 0.2, 1) forwards`,
      cursor: "default",
    },
  });

  const SectionsContainer = styled(Box)({
    position: "absolute",
    top: "20%",
    right: "8%",
    // bottom: "10%",
    width: "17%",
    display: "flex",
    flexDirection: "column",
    gap: "40px",
    overflow: "hidden",
    "@media (max-width: 1200px)": {
      width: "50%",
      gap: "10px",
    },
    "@media (max-width: 768px)": {
      position: "relative",
      width: "100%",
      top: "auto",
      right: "auto",
      bottom: "auto",
    },
  });

  const SectionCard = styled(Paper)(({ bgGradient }) => ({
    color: "white",
    borderRadius: "16px",
    cursor: "pointer",
    textAlign: "center",
    alignItems: "center",
    background: bgGradient,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
    transform: "translateX(100px)",
    opacity: 0,
    animation: "slideInFromRight 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
    padding: "12px",
    "&:hover": {
      transform: "translateY(-8px) scale(1.05)",
      boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
    },
    "@keyframes slideInFromRight": {
      to: {
        transform: "translateX(0)",
        opacity: 1,
      },
    },
  }));

  const SectionTitle = styled(Typography)({
    fontSize: "1.2rem",
    fontWeight: 600,
    marginBottom: "3px",
    letterSpacing: "1px",
    textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
  });

  const SectionDescription = styled(Typography)({
    fontSize: "0.8rem",
    fontWeight: 400,
    opacity: 0.95,
    textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
  });

  const ModalContent = styled(Box)(({ theme }) => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    minWidth: "50%",
    width: "65.5%",
    maxHeight: "95%",
    backgroundColor: "#F5F5DC",
    borderRadius: "24px",
    padding: theme.spacing(4),
    boxShadow: "0 25px 80px rgba(0, 0, 0, 0.4)",
    overflow: "hidden",
    outline: "none",
    zIndex: 1300,
  }));

  // Bottom Modal Styled Components
  const BottomModalContent = styled(Box)(({ theme }) => ({
    position: "absolute",
    bottom: 0,
    left: "20%",
    right: "20%",
    top: "20%",
    backgroundColor: "white",
    borderTopLeftRadius: "24px",
    borderTopRightRadius: "24px",
    boxShadow: "0 -20px 60px rgba(0, 0, 0, 0.3)",
    outline: "none",
    zIndex: 1400,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    animation: "slideUp 0.5s ease-out forwards",
    "@keyframes slideUp": {
      from: {
        transform: "translateY(100%)",
      },
      to: {
        transform: "translateY(0)",
      },
    },
  }));

  const BottomModalHeader = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2),
    borderBottom: "2px solid #e0e0e0",
    position: "relative",
    backgroundColor: "#f8f9fa",
  }));

  const CloseHandle = styled(Box)({
    width: "40px",
    height: "4px",
    backgroundColor: "#999",
    borderRadius: "2px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#666",
      transform: "scale(1.1)",
    },
  });

  const ProjectContent = styled(Box)({
    flex: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  });

  const ImageModalContent = styled(Box)(({ theme }) => ({
    position: "absolute",
    top: "0%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "white",
    borderRadius: "16px",
    padding: theme.spacing(3),
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
    maxWidth: "85vw",
    maxHeight: "85vh",
    outline: "none",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    zIndex: 1400,
  }));

  const FullImage = styled("img")({
    maxWidth: "100%",
    maxHeight: "60vh",
    borderRadius: "12px",
    objectFit: "contain",
  });

  const BackButton = styled(Button)({
    position: "absolute",
    top: "20px",
    left: "20px",
    zIndex: 1000,
    color: "white",
    border: "2px solid rgba(255, 255, 255, 0.4)",
    padding: "10px 24px",
    borderRadius: "25px",
    fontSize: "0.95rem",
    cursor: "pointer",
    background: "rgba(15, 23, 42, 0.8)",
    backdropFilter: "blur(15px)",
    boxShadow: "0 8px 25px rgba(45, 134, 89, 0.3)",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    fontWeight: 600,
    "&:hover": {
      borderColor: "rgba(255, 255, 255, 0.8)",
      transform: "translateY(-3px)",
      boxShadow:
        " 0 0 0 0 rgba(139, 90, 43, 0.7), 0 0 0 0 rgba(139, 90, 43, 0.7)",
      background: "rgba(139, 90, 43, 0.7), 0 0 0 0 rgba(139, 90, 43, 0.7)",
    },
  });

  // Video Container Components
  const VideoContainer = styled(Box)(({ theme }) => ({
    position: "absolute",
    top: "20%",
    left: "27.5%",
    width: "45%",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 20px 40px rgba(45, 134, 89, 0.3)",
    backgroundColor: "#000",
    transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
    opacity: 0,
    transform: "translateY(50px)",
    animation: isVideoVisible
      ? "slideUpVideo 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards"
      : "slideDownVideo 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards",
    "@keyframes slideUpVideo": {
      to: {
        opacity: 1,
        transform: "translateY(0)",
      },
    },
    "@keyframes slideDownVideo": {
      from: {
        opacity: 1,
        transform: "translateY(0)",
      },
      to: {
        opacity: 0,
        transform: "translateY(50px)",
      },
    },
    "@media (max-width: 1200px)": {
      width: "40%",
    },
    "@media (max-width: 768px)": {
      position: "relative",
      width: "90%",
      margin: "20px auto",
      bottom: "auto",
      left: "auto",
    },
  }));

  const VideoWrapper = styled(Box)({
    position: "relative",
    width: "100%",
    paddingBottom: "56.25%",
    backgroundColor: "#000",
  });

  const VideoElement = styled("video")({
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "block",
    backgroundColor: "#000",
  });

  const VideoTitle = styled(Typography)({
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    padding: "12px 16px",
    background: "linear-gradient(to top, rgba(0,0,0,0.8), transparent)",
    color: "white",
    fontSize: "0.95rem",
    fontWeight: 600,
    zIndex: 5,
  });

  return (
    <PageWrapper>
      <BackButton startIcon={<ArrowBack />} onClick={() => navigate("/")}>
        Back
      </BackButton>

      {/* Centered Image */}
      <CenteredLogo
        className={showSections ? "moved-left" : ""}
        onClick={handleImageClick}
      />
      {isVideoVisible && (
        <VideoContainer>
          <VideoWrapper>
            <VideoElement
              ref={videoRef}
              src="videos/SuthraMain.mp4"
              onEnded={handleVideoEnd}
              controls={true}
              preload="metadata"
              autoPlay
              muted
            />
            <VideoTitle>Suthra Punjab - Waste Management Initiative</VideoTitle>
          </VideoWrapper>
        </VideoContainer>
      )}

      {/* Sections Container */}
      {showSections && (
        <SectionsContainer>
          {sections.map((section, index) => (
            <SectionCard
              key={section.id}
              bgGradient={section.bgGradient}
              onClick={() => handleSectionClick(section.id)}
              sx={{
                animationDelay: `${index * 0.2 + 0.5}s`,
              }}
            >
              <SectionTitle>
                {section.icon}
                {section.title}
              </SectionTitle>
              <SectionDescription>{section.description}</SectionDescription>
            </SectionCard>
          ))}
        </SectionsContainer>
      )}

      {/* Main Modal for sections */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            sx: {
              zIndex: 1200,
            },
          },
        }}
        closeAfterTransition
      >
        <Fade in={openModal} timeout={500}>
          <ModalContent>
            {/* Close Button */}
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                color: "#2d8659",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "#e8f5e9",
                  transform: "rotate(90deg)",
                },
                zIndex: 10,
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Features Modal Content */}
            {selectedSection === "features" && <KeyFeatures />}

            {/* Process Modal Content */}
            {selectedSection === "process" && <SuthraPunjabProcess />}

            {/* Impact Modal Content */}
            {selectedSection === "impact" && <Impact />}

            {/* Smart Waste Management System Modal Content */}
            {selectedSection === "DigitalMonitoring" && (
              <DigitalMonitoring onImageClick={handleImageClick2} />
            )}

            {/* Waste-to-Value Projects Modal Content */}
            {selectedSection === "Waste-to-ValueProjects" && (
              <WasteToValueProjects onProjectClick={handleButtonClick} />
            )}

            {/* Community Mobilization Modal Content */}
            {selectedSection === "community-mobilization" && (
              <CommunityMobilization onProjectClick={handleButtonClick} />
            )}
          </ModalContent>
        </Fade>
      </Modal>

      {/* Bottom Project Modal */}
      <Modal
        open={openProjectModal}
        onClose={handleCloseProjectModal}
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
        }}
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            sx: {
              zIndex: 1300,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
            },
          },
        }}
        closeAfterTransition
      >
        <Fade in={openProjectModal} timeout={500}>
          <BottomModalContent>
            {/* Close Handle */}
            <BottomModalHeader>
              <CloseHandle onClick={handleCloseProjectModal} />
            </BottomModalHeader>

            {/* Project Content */}
            <ProjectContent>
              {selectedProject && selectedProject.component && (
                <selectedProject.component
                  onClose={handleCloseProjectModal}
                  isModal={true}
                />
              )}
            </ProjectContent>
          </BottomModalContent>
        </Fade>
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        open={openImageModal}
        onClose={handleCloseImageModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            sx: {
              zIndex: 1350,
            },
          },
        }}
        closeAfterTransition
      >
        <Fade in={openImageModal} timeout={500}>
          <ImageModalContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: "#2d8659", fontWeight: 700 }}
              >
                Image Preview
              </Typography>
              <IconButton
                onClick={handleCloseImageModal}
                sx={{
                  color: "#2d8659",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#e8f5e9",
                    transform: "rotate(90deg)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            {selectedImage && <FullImage src={selectedImage} alt="Preview" />}
          </ImageModalContent>
        </Fade>
      </Modal>
    </PageWrapper>
  );
};

export default SuthraPunjab;
